import { Box, FormLabel, Modal, Tooltip, Typography } from '@mui/material';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import Grid from '@mui/material/Grid2';
import Select from 'components/Common/Select';
import { MdInfoOutline } from 'react-icons/md';
import UploadFile from 'components/Common/UploadFile';
import Switch from 'components/Common/Switch';
import { useEffect, useState } from 'react';
import {
  useCreateOrUpdateDataSet,
  useGetDataSet,
  useGetKnowledgeBaseParser
} from 'apis/business/setting/knowledgeBase/hook';
import Spinner from 'components/Common/Spinner';
import { DataSetFormRequest, DataSetSchema } from 'utils/schema/validator/DataSetValidation';
import { zodResolver } from '@hookform/resolvers/zod';

export default function DataSetForm() {
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const [searchParams] = useSearchParams();

  const datasetType = searchParams.get('datasetType');
  const isBlankDatasetType = datasetType === 'blank';

  const [selectedFiles, setSelectedFiles] = useState<null | any[]>(null);
  const { data: knowledgeBaseParser, isLoading: isKnowledgeBaseParserLoading } = useGetKnowledgeBaseParser();
  const { data: dataset, isLoading: isDataSetLoading } = useGetDataSet();
  const { mutateAsync: handleDataSet, isPending } = useCreateOrUpdateDataSet();

  const handleChange = async (files: File[]) => {
    const data = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      data.push(file);
    }
    setSelectedFiles(data);
  };

  const { handleSubmit, control, watch, reset } = useForm<DataSetFormRequest>({
    defaultValues: { datasource_type: 'naive' },
    values: dataset ?? { name: '', datasource_type: 'naive', parser_config: {} },
    resolver: zodResolver(DataSetSchema)
  });

  const active = watch('datasource_type');
  const nameValue = watch('name');

  useEffect(() => {
    if (!dataset) {
      reset({
        name: nameValue,
        datasource_type: active,
        parser_config: knowledgeBaseParser?.[active ?? 'naive']
      });
    }
  }, [reset, knowledgeBaseParser, active, nameValue, dataset]);

  const onSubmit = (data: any) => {
    const formData = new FormData();
    if (isBlankDatasetType) {
      formData.append('dataset[datasource_type]', 'blank');
      formData.append('dataset[name]', data?.name);
    } else {
      prepareFormData(formData, data, 'dataset');
      if (selectedFiles) {
        selectedFiles.forEach((file) => {
          formData.append('documents[]', file);
        });
      }
    }
    handleDataSet(formData);
  };

  const selectedDatasourceType = watch('datasource_type', 'naive');

  const allowedFiles = allowedFilesMap[selectedDatasourceType] || [];

  if (isKnowledgeBaseParserLoading || isDataSetLoading) {
    return <Spinner />;
  }

  return (
    <Modal
      open={true}
      onClose={() => navigate(-1)}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={datasetId || isBlankDatasetType ? style2 : style}>
        <Box
          padding='16px 24px'
          display='flex'
          justifyContent='space-between'
          borderBottom='1px solid #F3F3F3'
          alignItems='center'
          position='sticky'
          bgcolor='white'
          top={0}
          zIndex={10000}
        >
          <Typography variant='h6'>Configuration</Typography>
          <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)}>
            <IoClose />
          </Box>
        </Box>
        <Box component='form' onSubmit={handleSubmit(onSubmit)}>
          <Grid container padding={3} spacing={3} alignItems='center'>
            <Grid size={datasetId || isBlankDatasetType ? 12 : 4}>
              <Input name='name' label='Name' control={control} fullWidth required sx={{ marginTop: 0.5 }} />
            </Grid>
            {!datasetId && !isBlankDatasetType && (
              <>
                <Grid size={4}>
                  <Select
                    name='datasource_type'
                    label='Chunk Method'
                    defaultValue='General'
                    control={control}
                    options={dataSourceOptions}
                    onChange={(e) =>
                      reset({
                        name: nameValue,
                        datasource_type: e.target.value as
                          | 'naive'
                          | 'website'
                          | 'book'
                          | 'laws'
                          | 'manual'
                          | 'one'
                          | 'paper'
                          | 'presentation'
                          | 'qa'
                          | 'resume'
                          | '_table'
                          | undefined,
                        parser_config:
                          knowledgeBaseParser?.[(e.target.value as keyof typeof knowledgeBaseParser) ?? 'naive']
                      })
                    }
                  />
                </Grid>
                {knowledgeBaseParser?.[selectedDatasourceType]?.chunk_token_num && (
                  <Grid size={4}>
                    <Input
                      name='parser_config.chunk_token_num'
                      label='Chunk Token Number'
                      control={control}
                      fullWidth
                    />
                  </Grid>
                )}
                {knowledgeBaseParser?.[selectedDatasourceType]?.delimiter && (
                  <Grid size={4}>
                    <Input name='parser_config.delimiter' label='Delimiter' control={control} fullWidth />
                  </Grid>
                )}
                {knowledgeBaseParser?.[selectedDatasourceType]?.delimiter && (
                  <Grid size={4}>
                    <Box display='flex' alignItems='center' gap='4px'>
                      <FormLabel>Layout Recognize</FormLabel>
                      <Switch name='parser_config.layout_recognize' control={control} />
                      <Tooltip
                        arrow
                        title='Use visual models for layout analysis to better identify document structure, find where the titles, text blocks, images, and tables are. Without this feature, only the plain text of the PDF can be obtained.'
                      >
                        <Box display='flex'>
                          <MdInfoOutline />
                        </Box>
                      </Tooltip>
                    </Box>
                  </Grid>
                )}
                {knowledgeBaseParser?.[active] && 'raptor' in knowledgeBaseParser[active] && (
                  <Grid size={4}>
                    <Box display='flex' alignItems='center' gap='4px'>
                      <FormLabel>Use Raptor</FormLabel>
                      <Switch name='parser_config.raptor.use_raptor' control={control} />
                      <Tooltip
                        arrow
                        title='Recursive Abstraction Processing for Tree-Organized Retrieval, see https://huggingface.co/papers/2401.18059 for more information'
                      >
                        <Box display='flex'>
                          <MdInfoOutline />
                        </Box>
                      </Tooltip>
                    </Box>
                  </Grid>
                )}
                {[
                  'naive',
                  'qa',
                  'manual',
                  'paper',
                  'book',
                  'laws',
                  'presentation',
                  '_table',
                  'one',
                  'website'
                ].includes(watch('datasource_type')) &&
                  watch('parser_config.raptor.use_raptor') && (
                    <Grid container spacing={3}>
                      <Input
                        multiline
                        minRows={4}
                        name='parser_config.raptor.prompt'
                        label='Prompt'
                        fullWidth
                        control={control}
                      />
                      <Grid container spacing={3}>
                        <Grid size={3}>
                          <Input name='parser_config.raptor.max_token' label='Max Token' control={control} fullWidth />
                        </Grid>
                        <Grid size={3}>
                          <Input name='parser_config.raptor.threshold' label='Threshold' control={control} fullWidth />
                        </Grid>
                        <Grid size={3}>
                          <Input
                            name='parser_config.raptor.max_cluster'
                            label='Max Cluster'
                            control={control}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={3}>
                          <Input
                            name='parser_config.raptor.random_seed'
                            label='Random Seed'
                            control={control}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                <Grid overflow='auto' size={12}>
                  {watch('datasource_type') !== 'website' && (
                    <UploadFile
                      selectedFiles={selectedFiles}
                      handleChange={handleChange}
                      allowedFileTypes={allowedFiles}
                      datasourceType={watch('datasource_type')}
                    />
                  )}
                  {watch('datasource_type') === 'website' && (
                    <Input name='parser_config.url' label='Url' control={control} fullWidth />
                  )}
                </Grid>
              </>
            )}
          </Grid>
          <Box
            display='flex'
            justifyContent='end'
            gap='16px'
            padding='16px 24px'
            borderTop='1px solid #F3F3F3'
            position='sticky'
            bottom={0}
            zIndex={100}
            bgcolor='white'
          >
            <Button
              color='secondary'
              variant='outlined'
              sx={{ border: '1px solid #F3F3F3' }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button type='submit' variant='contained' isPending={isPending}>
              {datasetId ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  borderRadius: '8px',
  overflow: 'auto',
  maxHeight: '90vh',
  width: {
    xs: '90%',
    sm: 400,
    md: 600,
    lg: 1000
  }
};
const style2 = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  borderRadius: '8px',
  overflow: 'auto',
  maxHeight: '90vh',
  width: {
    xs: '90%',
    sm: 400,
    md: 500
  }
};

export const allowedFilesMap: {
  [key: string]: string[];
} = {
  naive: ['docx', 'xlsx', 'pdf', 'txt', 'md', 'json', 'html'],
  qa: ['xlsx', 'csv'],
  resume: ['docx', 'pdf', 'txt'],
  manual: ['pdf'],
  _table: ['xlsx', 'csv', 'txt'],
  paper: ['pdf'],
  book: ['docx', 'pdf', 'txt'],
  laws: ['docx', 'pdf', 'txt'],
  presentation: ['pdf', 'pptx'],
  one: ['docx', 'xlsx', 'pdf', 'txt']
};

const dataSourceOptions = [
  { value: 'naive', label: 'General' },
  { value: 'qa', label: 'Q&A' },
  { value: 'manual', label: 'Manual' },
  { value: 'paper', label: 'Paper' },
  // TODO - > need to check
  // { value: 'presentation', label: 'Presentation' },
  // { value: 'resume', label: 'Resume' },
  { value: '_table', label: 'Table' },
  { value: 'book', label: 'Book' },
  { value: 'laws', label: 'Laws' },
  { value: 'one', label: 'One' }
];

const prepareFormData = (formData: FormData, data: Record<string, any>, parentKey: string = '') => {
  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      const value = data[key];
      const formKey = parentKey ? `${parentKey}[${key}]` : key;

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        prepareFormData(formData, value, formKey);
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          prepareFormData(formData, { [`${key}[${index}]`]: item }, parentKey);
        });
      } else {
        formData.append(formKey, value);
      }
    }
  }
};
